/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import api from "../../api";
import { CrossIconContainer, StyledDialogTitle, LoaderContainer } from "../audit_log_modal/styles";
import { IndexTable } from "../index_table";
import { MODAL_TABLE_MAX_HEIGHT } from "../index_table/styles";
import { Loader } from "../loader";
import { OrderAppliedDiscountModal } from "../order_discount_modal";
import { useNotificationContext } from "../../contexts/notificationContext";
import { Query_Keys } from "../../lib/queryKeys";

// Shared shell for the Check-In Compliance row drill-ins: one restaurant's list under the report's applied filters,
// sorted and paged on the server, with the order detail modal behind each row's eye icon.
export const CheckInComplianceDrillInModal = ({
  open,
  handleClose,
  restaurant,
  apiFilters,
  kind,
  title,
  testIdPrefix,
  columns,
  buildRow,
  type,
  errorMessage,
}) => {
  const showNotification = useNotificationContext();
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedOrder, setSelectedOrder] = useState("");
  const [orderOpen, setOrderOpen] = useState(false);

  const restaurantId = restaurant?.id;

  const fetchList = async () => {
    const response = await api.get(`/v1/admin/check_in_compliance/${restaurantId}/${kind}`, {
      params: {
        ...apiFilters,
        page,
        per_page: itemsPerPage,
        sort_by: sortBy || undefined,
        sort_order: sortBy ? sortOrder : undefined,
      },
    });
    return {
      data: response?.data || [],
      pageCount: Number(response?.headers?.["total-count"] || 0),
    };
  };

  const { data: list, isFetching, error } = useQuery({
    queryKey: Query_Keys[`check_in_compliance_${kind}`]({
      restaurantId,
      queryFilter: apiFilters,
      page,
      itemsPerPage,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchList,
    enabled: Boolean(open && restaurantId),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (!error) return;
    showNotification(error?.response?.data?.error || errorMessage, "error");
  }, [error, errorMessage, showNotification]);

  const fetchOrderDetail = async () => {
    const response = await api.get(`/v1/admin/orders/${selectedOrder?.order_id}/detail`);
    return response?.data;
  };

  const { data: orderData, isLoading: orderLoading } = useQuery({
    queryKey: Query_Keys.applied_discount({ selected_order: selectedOrder?.order_id }),
    queryFn: fetchOrderDetail,
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(selectedOrder?.order_id),
  });

  const handleOrderOpen = (order) => {
    setSelectedOrder(order);
    setOrderOpen(true);
  };

  const handleSortChange = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    setPage(1);
  };

  const rows = (list?.data || []).map((record) => buildRow(record, handleOrderOpen));

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth={true}
        maxWidth={"lg"}
        data-testid={testIdPrefix}
      >
        <CrossIconContainer>
          <ClearIcon onClick={handleClose} data-testid={`${testIdPrefix}-close-icon`} />
        </CrossIconContainer>
        <StyledDialogTitle data-testid={`${testIdPrefix}-title`}>{title}</StyledDialogTitle>
        <DialogContent>
          {isFetching ? (
            <LoaderContainer>
              <Loader />
            </LoaderContainer>
          ) : (
            <IndexTable
              maxHeight={MODAL_TABLE_MAX_HEIGHT}
              columns={columns}
              data={rows}
              tableId={`check-in-compliance-${kind.replace(/_/g, "-")}`}
              type={type}
              report={false}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              page={page}
              setPage={setPage}
              pageCount={list?.pageCount || 0}
              sortMode="server"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
              emptyStateText={error ? errorMessage : undefined}
            />
          )}
        </DialogContent>
      </Dialog>

      <OrderAppliedDiscountModal
        open={orderOpen}
        handleClose={() => setOrderOpen(false)}
        ordersData={orderData}
        isLoading={orderLoading}
      />
    </React.Fragment>
  );
};
